import { Box, Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import isEqual from 'fast-deep-equal/es6/react';

import { InfoTooltip } from '../../../components/core';
import {
  ConfigResponse,
  FeeValue,
  AcceptedCurrency,
  isFeeWithValue,
} from '../../../store/modules/merchant/types';
import { ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { inheritableLabelsFromConfigNames } from '../../../util/parentSettingSave';
import Switch from '../../../common/Switch';
import { FeeForm } from './FeeForm';

const buildFeeValue = (feeType: FeeValue['feeType'], localValue: Partial<FeeValue>): FeeValue => {
  const baseFeeValue = {
    feeType,
    description: localValue.description ?? '',
    style: localValue.style ?? 'PERCENTAGE',
  } as FeeValue;

  if (baseFeeValue.style === 'EXTERNAL') {
    return {
      ...baseFeeValue,
      style: 'EXTERNAL',
      credentialType: 'payment',
    } as FeeValue;
  }

  const feeValue = {
    ...baseFeeValue,
    value: (localValue as Partial<FeeValue> & { value: number }).value ?? 0,
  } as FeeValue;

  if (feeValue.style === 'FLAT' && localValue.style === 'FLAT') {
    feeValue.style = 'FLAT';
    feeValue.currencyCode = localValue.currencyCode ?? 'USD';
  }

  return feeValue;
};

type OnSaveConfigs = {
  generalFeeConfig?: { value: FeeValue; enabled: boolean };
  surchargeFeeConfig?: { value: FeeValue; enabled: boolean };
  discountsConfig?: { enabled: boolean };
};

type Props = {
  generalConfig?: ConfigResponse<FeeValue>;
  surchargeConfig?: ConfigResponse<FeeValue>;
  discountsConfig?: ConfigResponse<boolean>;
  onSave: (configs: OnSaveConfigs) => Promise<void>;
  readOnly?: boolean;
  isSaving?: boolean;
  acceptedCurrency?: ConfigResponse<AcceptedCurrency>;
  isWorldpayFeeAssistToggleEnabled?: boolean;
};

export const FeesContent = ({
  surchargeConfig,
  generalConfig,
  discountsConfig,
  onSave,
  isSaving,
  readOnly = false,
  acceptedCurrency,
  isWorldpayFeeAssistToggleEnabled = false,
}: Props) => {
  const [localSurchargeConfig, setLocalSurchargeConfig] = useState<Partial<FeeValue>>(
    surchargeConfig?.value ?? {},
  );
  const [localGeneralConfig, setLocalGeneralConfig] = useState<Partial<FeeValue>>(
    generalConfig?.value ?? {},
  );

  const [isLocalSurchargeEnabled, setIsLocalSurchageEnabled] = useState(!!surchargeConfig?.enabled);
  const [isLocalGeneralEnabled, setIsLocalGeneralEnabled] = useState(!!generalConfig?.enabled);
  const [isLocalDiscountsEnabled, setIsLocalDiscountsEnabled] = useState(!!discountsConfig?.value);

  const [hasErrorSurchargeValue, setHasErrorSurchargeValue] = useState(false);
  const [hasErrorGeneralValue, setHasErrorGeneralValue] = useState(false);

  const [useExternalSurcharge, setUseExternalSurcharge] = useState(
    surchargeConfig?.value?.style === 'EXTERNAL',
  );

  // save the previous surcharge style when toggling the Worldpay Fee Assist option
  const [previousSurchargeStyle, setPreviousSurchargeStyle] =
    useState<FeeValue['style']>('PERCENTAGE');

  const handleExternalSurchargeToggle = useCallback(
    (enabled: boolean) => {
      setUseExternalSurcharge(enabled);
      setLocalSurchargeConfig((prev) => {
        if (enabled) {
          setPreviousSurchargeStyle(prev.style ?? 'PERCENTAGE');
          return {
            ...prev,
            style: 'EXTERNAL',
          };
        } else {
          return {
            ...prev,
            style: previousSurchargeStyle,
          };
        }
      });
    },
    [previousSurchargeStyle],
  );

  const isGeneralConfigEqual = useMemo(
    () =>
      isEqual(
        {
          enabled: isLocalGeneralEnabled,
          value: buildFeeValue('GENERAL_FEE', localGeneralConfig),
        },
        {
          enabled: !!generalConfig?.enabled,
          value: buildFeeValue('GENERAL_FEE', generalConfig?.value ?? {}),
        },
      ),
    [generalConfig?.enabled, generalConfig?.value, isLocalGeneralEnabled, localGeneralConfig],
  );

  const isSurchargeConfigEqual = useMemo(
    () =>
      isEqual(
        {
          enabled: isLocalSurchargeEnabled,
          value: buildFeeValue('SURCHARGE_FEE', localSurchargeConfig),
        },
        {
          enabled: !!surchargeConfig?.enabled,
          value: buildFeeValue('SURCHARGE_FEE', surchargeConfig?.value ?? {}),
        },
      ),
    [
      isLocalSurchargeEnabled,
      localSurchargeConfig,
      surchargeConfig?.enabled,
      surchargeConfig?.value,
    ],
  );

  const isDiscountsConfigEqual = useMemo(
    () => isLocalDiscountsEnabled === !!discountsConfig?.value,
    [isLocalDiscountsEnabled, discountsConfig?.value],
  );

  const handleSave = useCallback(async () => {
    const configs: OnSaveConfigs = {};
    if (!isGeneralConfigEqual) {
      if (
        isFeeWithValue(localGeneralConfig) &&
        !localGeneralConfig.value &&
        localGeneralConfig.value !== 0
      ) {
        setHasErrorGeneralValue(true);
        return;
      }

      configs.generalFeeConfig = {
        value: buildFeeValue('GENERAL_FEE', localGeneralConfig),
        enabled: isLocalGeneralEnabled,
      };
    }

    if (!isSurchargeConfigEqual) {
      if (
        isFeeWithValue(localSurchargeConfig) &&
        !localSurchargeConfig.value &&
        localSurchargeConfig.value !== 0
      ) {
        setHasErrorSurchargeValue(true);
        return;
      }

      configs.surchargeFeeConfig = {
        value: buildFeeValue('SURCHARGE_FEE', localSurchargeConfig),
        enabled: isLocalSurchargeEnabled,
      };
    }

    if (!isDiscountsConfigEqual) {
      configs.discountsConfig = { enabled: isLocalDiscountsEnabled };
    }

    await onSave(configs);
  }, [
    isGeneralConfigEqual,
    isLocalGeneralEnabled,
    isLocalSurchargeEnabled,
    isLocalDiscountsEnabled,
    isSurchargeConfigEqual,
    isDiscountsConfigEqual,
    localGeneralConfig,
    localSurchargeConfig,
    onSave,
  ]);

  const resetValues = useCallback(() => {
    setLocalGeneralConfig(generalConfig?.value ?? {});
    setLocalSurchargeConfig(surchargeConfig?.value ?? {});
    setIsLocalGeneralEnabled(!!generalConfig?.enabled);
    setIsLocalSurchageEnabled(!!surchargeConfig?.enabled);
    setIsLocalDiscountsEnabled(!!discountsConfig?.value);
    setUseExternalSurcharge(surchargeConfig?.value?.style === 'EXTERNAL');
    setPreviousSurchargeStyle('PERCENTAGE');
    setHasErrorGeneralValue(false);
    setHasErrorSurchargeValue(false);
  }, [
    generalConfig?.enabled,
    generalConfig?.value,
    surchargeConfig?.enabled,
    surchargeConfig?.value,
    discountsConfig?.value,
  ]);

  const isSaveBarOpen = useMemo(
    () => !isGeneralConfigEqual || !isSurchargeConfigEqual || !isDiscountsConfigEqual,
    [isGeneralConfigEqual, isSurchargeConfigEqual, isDiscountsConfigEqual],
  );

  const inheritableSaveBarLabels = useMemo(
    () => (isDiscountsConfigEqual ? [] : inheritableLabelsFromConfigNames(['DISCOUNTS'])),
    [isDiscountsConfigEqual],
  );

  return (
    <>
      <Stack spacing={3}>
        <Stack spacing={2}>
          <ConfigSwitchSection
            title="Surcharging Fees"
            description={`
              Set a percentage or dollar amount as a general fee to be added to customer orders. You may add a description to the general fee that will appear in a tooltip to your customers during checkout.
              When Worldpay Fee Assist option is enabled, the surcharging fee will be calculated by Worldpay during checkout instead.
              \n As a merchant, there are surcharging laws and rules you must follow. Please confirm with your payment service provider and/or legal counsel to confirm the settings you should use for your merchant account.
              `}
            isChecked={isLocalSurchargeEnabled}
            onToggle={() => setIsLocalSurchageEnabled((prev) => !prev)}
            disabled={readOnly}
            plural
            switchTestId="SURCHARGE_FEE_SWITCH"
          />
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="small">Worldpay Fee Assist</Typography>
              <InfoTooltip
                title="Worldpay Fee Assist can only be used when Express is set as current payment gateway."
                placement="right"
                ariaLabel="Worldpay Fee Assist details"
              />
            </Stack>
            <Switch
              isChecked={useExternalSurcharge}
              onToggle={handleExternalSurchargeToggle}
              isDisabled={readOnly || !isLocalSurchargeEnabled || !isWorldpayFeeAssistToggleEnabled}
              data-testid="USE_EXTERNAL_SURCHARGE_SWITCH"
            />
          </Stack>
          <FeeForm
            feeType="SURCHARGE_FEE"
            localFeeValue={localSurchargeConfig}
            setLocalFeeValue={setLocalSurchargeConfig}
            error={hasErrorSurchargeValue}
            setError={setHasErrorSurchargeValue}
            disabled={!isLocalSurchargeEnabled || readOnly || useExternalSurcharge}
            currency={acceptedCurrency?.value}
          />
        </Stack>
        <Box>
          <ConfigSwitchSection
            title="General Fees"
            description="Set a percentage or dollar amount as a general fee to be added to customer orders. You may add a description to the general fee that will appear in a tooltip to your customers during checkout."
            isChecked={isLocalGeneralEnabled}
            onToggle={() => setIsLocalGeneralEnabled((prev) => !prev)}
            disabled={readOnly}
            plural
            switchTestId="GENERAL_FEE_SWITCH"
          />
          <FeeForm
            feeType="GENERAL_FEE"
            localFeeValue={localGeneralConfig}
            setLocalFeeValue={setLocalGeneralConfig}
            error={hasErrorGeneralValue}
            setError={setHasErrorGeneralValue}
            disabled={!isLocalGeneralEnabled || readOnly}
            currency={acceptedCurrency?.value}
          />
        </Box>
        <Box>
          <ConfigSwitchSection
            title="Discounts"
            description="Enable discounts to be applied to customer orders during checkout. This feature is not available in Paylink checkouts."
            isChecked={isLocalDiscountsEnabled}
            onToggle={() => setIsLocalDiscountsEnabled((prev) => !prev)}
            disabled={readOnly}
            plural
            switchTestId="DISCOUNTS_SWITCH"
          />
        </Box>
      </Stack>
      <ParentAwareSaveBar
        disableSave={readOnly || isSaving || hasErrorGeneralValue || hasErrorSurchargeValue}
        saveLoading={isSaving}
        show={isSaveBarOpen && !readOnly}
        onCancel={() => resetValues()}
        inheritableSettingLabels={inheritableSaveBarLabels}
        onSave={handleSave}
      />
    </>
  );
};
